import { yupResolver } from '@hookform/resolvers/yup'
import React from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Divider, Group, Space, Title } from '@mantine/core'
import { Alert, Button } from '@/components/Elements'
import { LocationFormSection } from '@/features/profile/components/FormSections'
import { useFormSubmit } from '@/hooks'
import { CompanySection, GeneralSection } from './Sections'
import { validationSchema } from './validation'

type FormValues = {
  company_name: string
  company_uid: string
  company_function?: string

  gender: string
  name: string
  last_name: string
  email: string
  phone: string

  street: string
  street_number: string
  zip: string
  city: string
}

interface IProps {
  onSubmit: (values: object) => Promise<void>
  initialValues?: object
}

export const ProfileForm = ({ initialValues, ...props }: IProps) => {
  const { t } = useTranslation()

  const methods = useForm<FormValues>({
    defaultValues: {
      company_name: '',
      company_uid: '',
      company_function: '',
      name: '',
      last_name: '',
      email: '',
      phone: '',
      street: '',
      street_number: '',
      zip: '',
      city: '',
      ...initialValues,
    },
    resolver: yupResolver(validationSchema as any),
  })

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods

  const { error: submitError, onSubmit: onFormSubmit } = useFormSubmit({
    submit: props.onSubmit,
    setError,
  })

  const onSubmit: SubmitHandler<any> = async (data) => {
    try {
      const { phone, ...resetValues } = data

      const values = {
        ...resetValues,
        phone: phone ? phone.replace(/[^0-9]/g, '') : '',
      }

      await onFormSubmit(values)
    } catch (error) {}
  }

  return (
    <>
      <FormProvider {...methods}>
        {submitError && (
          <Alert type={'error'} mb={'sm'}>
            {submitError?.data?.message || t('error')}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <CompanySection />

          <GeneralSection />

          <Box mb={'md'}>
            <Title mb={'xs'} order={4}>
              {t('location')}
            </Title>
            <LocationFormSection />
            <Divider />
          </Box>

          <Space h={'xl'} />

          <Group mt={'xl'} position={'right'}>
            <Button type={'submit'} loading={isSubmitting}>
              {t('save')}
            </Button>
          </Group>
        </form>
      </FormProvider>
    </>
  )
}
